/** Iter 233 · Weekly PIP pulse — assignee submits mood + short reflections.
 *  Rendered inside PIPStatusCard when the user has an active plan.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Send, Smile, Meh, Frown } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const currentMonday = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));   // shift to Monday
  return d.toISOString().slice(0, 10);
};

const moodOpts = [
  { key: "green", label: "Going well", Icon: Smile, cls: "bg-emerald-50 border-emerald-400 text-emerald-700" },
  { key: "amber", label: "Mixed",       Icon: Meh,   cls: "bg-amber-50 border-amber-400 text-amber-700" },
  { key: "red",   label: "Struggling",  Icon: Frown, cls: "bg-rose-50 border-rose-400 text-rose-700" },
];

export default function PIPWeeklyPulse({ planId }) {
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ mood: "green", what_went_well: "", what_blocked: "", need_help_with: "" });
  const week = currentMonday();

  useEffect(() => {
    if (!planId) return;
    api.get(`/pip-plans/${planId}/pulses`)
      .then((r) => {
        const cur = (r.data || []).find((p) => p.week_of === week);
        if (cur) {
          setExisting(cur);
          setF({
            mood: cur.mood,
            what_went_well: cur.what_went_well || "",
            what_blocked: cur.what_blocked || "",
            need_help_with: cur.need_help_with || "",
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [planId, week]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/pip-plans/${planId}/pulses`, { ...f, week_of: week });
      setExisting(data);
      toast.success(existing ? "Pulse updated" : "Pulse submitted — your manager will see it in Monday's summary");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to submit"));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-xs text-muted-foreground italic p-3">Loading pulse…</div>;

  return (
    <div className="border-t border-amber-300 pt-3 space-y-3" data-testid="pip-weekly-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Weekly pulse</div>
          <div className="text-[11px] text-muted-foreground">Week of {week}{existing ? " · already submitted, feel free to update" : ""}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {moodOpts.map(({ key, label, Icon, cls }) => (
          <button
            key={key}
            type="button"
            onClick={() => setF((s) => ({ ...s, mood: key }))}
            className={`px-3 py-2 border text-xs font-medium flex items-center gap-2 transition ${f.mood === key ? cls : "bg-background border-border hover:border-primary"}`}
            data-testid={`pip-pulse-mood-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        <Textarea
          placeholder="What went well this week?"
          value={f.what_went_well}
          onChange={(e) => setF((s) => ({ ...s, what_went_well: e.target.value }))}
          rows={2}
          data-testid="pip-pulse-went-well"
        />
        <Textarea
          placeholder="What blocked you or slowed you down?"
          value={f.what_blocked}
          onChange={(e) => setF((s) => ({ ...s, what_blocked: e.target.value }))}
          rows={2}
          data-testid="pip-pulse-blocked"
        />
        <Textarea
          placeholder="Where would help unblock you?"
          value={f.need_help_with}
          onChange={(e) => setF((s) => ({ ...s, need_help_with: e.target.value }))}
          rows={2}
          data-testid="pip-pulse-need-help"
        />
      </div>
      <div className="flex items-center justify-end">
        <Button onClick={submit} disabled={saving} className="rounded-none" data-testid="pip-pulse-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          {existing ? "Update pulse" : "Submit pulse"}
        </Button>
      </div>
    </div>
  );
}
